import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import en from './en.json';
import kk from './kk.json';
import ru from './ru.json';

export type Language = 'ru' | 'kk' | 'en';

type Dictionary = Record<string, string>;

type I18nContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
};

const dictionaries: Record<Language, Dictionary> = { ru, kk, en };
const storageKey = 'ddproof.site.language';

const I18nContext = createContext<I18nContextValue | null>(null);

function getInitialLanguage(): Language {
  const urlLanguage = new URLSearchParams(window.location.search).get('lang');
  if (urlLanguage === 'ru' || urlLanguage === 'kk' || urlLanguage === 'en') {
    window.localStorage.setItem(storageKey, urlLanguage);
    document.documentElement.lang = urlLanguage;
    return urlLanguage;
  }

  const stored = window.localStorage.getItem(storageKey);
  if (stored === 'ru' || stored === 'kk' || stored === 'en') {
    document.documentElement.lang = stored;
    return stored;
  }

  document.documentElement.lang = 'en';
  return 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = useCallback((nextLanguage: Language) => {
    setLanguageState(nextLanguage);
    window.localStorage.setItem(storageKey, nextLanguage);
    document.documentElement.lang = nextLanguage;
  }, []);

  const t = useCallback(
    (key: string, params: Record<string, string> = {}) => {
      const template = dictionaries[language][key] ?? dictionaries.en[key] ?? key;
      return Object.entries(params).reduce(
        (value, [name, replacement]) => value.replaceAll(`{${name}}`, replacement),
        template,
      );
    },
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used inside I18nProvider');
  }
  return context;
}
